import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripeKey } from '../config/stripe';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import './Shop.css';

// Load Stripe
const stripePromise = loadStripe(getStripeKey());

// Print Size Options Component
const PrintSizeOptions = ({ item, onAddToCart, onClose, onOpenCart }) => {
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedType, setSelectedType] = useState('somerset');

  const printOptions = {
    'Fractured Flow - Print': [
      { size: '12x24', somerset: 85.00, canvas: 150.00 },
      { size: '24x48 unstretched canvas', somerset: 170.00, canvas: 210.00 }
    ],
    'The Sun Sets to Rise - Print': [
      { size: '12x24', somerset: 85.00, canvas: 150.00 },
      { size: '24x48 unstretched canvas', somerset: 170.00, canvas: 210.00 }
    ],
    'John 3:30 - Print': [
      { size: '16x24', somerset: 95.00, canvas: 171.00 },
      { size: '24x36 trimmed', somerset: 103.50, canvas: 256.00 }
    ],
    'Su-STAINED - Print': [
      { size: '18x24', somerset: 101.00, canvas: 182.00 },
      { size: '30x40 unstretched', somerset: 172.00, canvas: 250.00 }
    ],
    'Su-STAINED v2 - Print': [
      { size: '12x24', somerset: 101.00, canvas: 182.00 },
      { size: '24x48 unstretched canvas', somerset: 172.00, canvas: 250.00 }
    ]
  };

  const options = printOptions[item.title] || [];
  const lowestPrice = options.length > 0 ? Math.min(...options.map(opt => opt.somerset)) : 0;

  const handleAddToCart = () => {
    if (!selectedSize) return;
    
    const selectedOption = options.find(opt => opt.size === selectedSize);
    const price = selectedType === 'somerset' ? selectedOption.somerset : selectedOption.canvas;
    // Google Analytics event
    if (window.gtag) {
      window.gtag('event', 'add_to_cart', {
        event_category: 'ecommerce',
        event_label: item.title,
        item_id: item.id,
        item_name: item.title,
        item_category: item.category,
        price: price,
        size: selectedSize,
        type: selectedType
      });
    }
    onAddToCart({
      ...item,
      selectedSize,
      selectedType,
      price: `$${price.toFixed(2)}`,
      priceInCents: Math.round(price * 100),
      fullTitle: `${item.title} - ${selectedSize} (${selectedType === 'somerset' ? 'Somerset Giclée' : 'Canvas Giclée'})`
    });
    onOpenCart(); // Open cart slideout immediately
    onClose();
  };

  return (
    <div className="print-options-modal">
      <h3>Select Print Size & Type</h3>
      <div className="print-options">
        <div className="size-options">
          <label>Size:</label>
          <select value={selectedSize} onChange={(e) => setSelectedSize(e.target.value)}>
            <option value="">Choose size...</option>
            {options.map((option, index) => (
              <option key={index} value={option.size}>
                {option.size} - ${option.somerset} (Somerset) / ${option.canvas} (Canvas)
              </option>
            ))}
          </select>
        </div>
        
        {selectedSize && (
          <div className="type-options">
            <label>Print Type:</label>
            <div className="type-buttons">
              <button
                className={selectedType === 'somerset' ? 'active' : ''}
                onClick={() => setSelectedType('somerset')}
              >
                Somerset Giclée
              </button>
              <button
                className={selectedType === 'canvas' ? 'active' : ''}
                onClick={() => setSelectedType('canvas')}
              >
                Canvas Giclée
              </button>
            </div>
          </div>
        )}
        
        {selectedSize && (
          <div className="selected-price">
            <strong>Price: ${selectedType === 'somerset' ? 
              options.find(opt => opt.size === selectedSize)?.somerset.toFixed(2) :
              options.find(opt => opt.size === selectedSize)?.canvas.toFixed(2)
            }</strong>
          </div>
        )}
        
        <button 
          className="add-to-cart-btn"
          onClick={handleAddToCart}
          disabled={!selectedSize}
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
};





const Shop = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [printOptionsModal, setPrintOptionsModal] = useState({ isOpen: false, item: null });
  const { addToCart, cart, openCart } = useCart();
  


  const categories = ['All', '1 of 1s', 'Prints'];

  const shopItems = [
    {
      id: 13,
      title: "Su-STAINED v2",
      category: "1 of 1s",
      image: "/images/gallery/su-stained-v2-new.jpg",
      price: "$2,500",
      priceInCents: 250000,
      availability: "1 of 1",
      sold: true,
      description: "It is still found in faith. A continuation of Su-STAINED through acrylic and gold luxer wax on canvas.",
      medium: "Mixed Media: Acrylic & Gold Luxer Wax on Canvas",
      dimensions: "24\" x 48\"",
      year: "2026"
    },
    {
      id: 14,
      title: "Su-STAINED v2 - Print",
      category: "Prints",
      image: "/images/gallery/su-stained-v2-new.jpg",
      price: "$101+",
      priceInCents: 10100,
      availability: "Limited Edition",
      sold: false,
      description: "A limited edition print of Su-STAINED v2, available in sizes scaled to the original 24\" x 48\" canvas.",
      medium: "Archival Print",
      dimensions: "Multiple sizes available",
      year: "2026"
    },
    {
      id: 15,
      title: "The Sun Sets to Rise",
      category: "1 of 1s",
      image: "/images/gallery/sun-sets-to-rise-new.jpg",
      price: "Commissioned — Price Unavailable",
      priceInCents: 0,
      availability: "1 of 1",
      sold: true,
      description: "Weeping may endure for a night, but joy comes in the morning. Acrylic, spray paint, and marker on canvas.",
      medium: "Mixed Media: Acrylic, Spray Paint & Marker on Canvas",
      dimensions: "24\" x 48\"",
      year: "2025"
    },
    {
      id: 16,
      title: "The Sun Sets to Rise - Print",
      category: "Prints",
      image: "/images/gallery/sun-sets-to-rise-new.jpg",
      price: "$85+",
      priceInCents: 8500,
      availability: "Limited Edition",
      sold: false,
      description: "A limited edition print of The Sun Sets to Rise, available in sizes scaled to the original 24\" x 48\" canvas.",
      medium: "Archival Print",
      dimensions: "Multiple sizes available",
      year: "2025"
    },
    {
      id: 1,
      title: "The Value is Still In You",
      category: "1 of 1s",
      image: "/images/gallery/value-is-still-new.png",
      price: "$5,000",
      priceInCents: 500000,
      availability: "1 of 1",
      sold: false,
      description: "A testament to inner worth and divine purpose. This powerful mixed media piece combines acrylic, gold luxer wax, spray paint, and denim on canvas.",
      medium: "Mixed Media: Acrylic, Gold Luxer Wax, Spray Paint & Denim on Canvas",
      dimensions: "48\" x 60\"",
      year: "2024"
    },

    {
      id: 3,
      title: "The Last of Me",
      category: "1 of 1s",
      image: "/images/gallery/hanging-on.new.png",
      price: "$2,500",
      priceInCents: 250000,
      availability: "1 of 1",
      sold: false,
      description: "A powerful exploration of identity and transformation using acrylic and yarn on canvas.",
      medium: "Mixed Media: Acrylic & Yarn on Canvas",
      dimensions: "30\" x 48\"",
      year: "2025"
    },
    {
      id: 4,
      title: "Fractured Flow",
      category: "1 of 1s",
      image: "/images/gallery/colorful-arms-new.png",
      price: "$1,000",
      priceInCents: 100000,
      availability: "1 of 1",
      sold: false,
      description: "The beauty in brokenness and the flow of healing through acrylic and spray paint on canvas.",
      medium: "Mixed Media: Acrylic & Spray Paint on Canvas",
      dimensions: "24\" x 48\"",
      year: "2024"
    },
    {
      id: 5,
      title: "Fractured Flow - Print",
      category: "Prints",
      image: "/images/gallery/colorful-arms-new.png",
      price: "$85+",
      priceInCents: 8500,
      availability: "Limited Edition",
      sold: false,
      description: "A limited edition print of this beautiful exploration of brokenness and healing.",
      medium: "Archival Print",
      dimensions: "Multiple sizes available",
      year: "2024"
    },
    {
      id: 6,
      title: "John 3:30",
      category: "1 of 1s",
      image: "/images/gallery/blue-guy-new.png",
      price: "$1,750",
      priceInCents: 175000,
      availability: "1 of 1",
      sold: false,
      description: "He must become greater; I must become less. A spiritual meditation in mixed media.",
      medium: "Mixed Media: Acrylic, Gold Luxer Wax on Canvas",
      dimensions: "30\" x 48\"",
      year: "2023"
    },
    {
      id: 7,
      title: "John 3:30 - Print",
      category: "Prints",
      image: "/images/gallery/blue-guy-new.png",
      price: "$95+",
      priceInCents: 9500,
      availability: "Limited Edition",
      sold: false,
      description: "A limited edition print of this spiritual meditation piece.",
      medium: "Archival Print",
      dimensions: "Multiple sizes available",
      year: "2023"
    },
    {
      id: 8,
      title: "Su-STAINED",
      category: "1 of 1s",
      image: "/images/gallery/colorful-face-new.png",
      price: "$2,000",
      priceInCents: 200000,
      availability: "1 of 1",
      sold: true,
      description: "The sustenance and strength found in faith through acrylic and gold luxer wax on canvas.",
      medium: "Mixed Media: Acrylic & Gold Luxer Wax on Canvas",
      dimensions: "36\" x 48\"",
      year: "2022"
    },
    {
      id: 9,
      title: "Su-STAINED - Print",
      category: "Prints",
      image: "/images/gallery/colorful-face-new.png",
      price: "$101+",
      priceInCents: 10100,
      availability: "Limited Edition",
      sold: false,
      description: "A limited edition print of this powerful piece about sustenance and strength.",
      medium: "Archival Print",
      dimensions: "Multiple sizes available",
      year: "2022"
    },
    {
      id: 10,
      title: "Eyes Watching",
      category: "1 of 1s",
      image: "/images/gallery/eyes-watching-new.png",
      price: "$750",
      priceInCents: 75000,
      availability: "1 of 1",
      sold: true,
      description: "The divine gaze that sees and knows all. This piece combines acrylic and glitter on canvas.",
      medium: "Mixed Media: Acrylic & Glitter on Canvas",
      dimensions: "48\" x 24\"",
      year: "2023"
    },
    {
      id: 11,
      title: "Life Within HER",
      category: "1 of 1s",
      image: "/images/gallery/flower-hug-new.png",
      price: "$950",
      priceInCents: 95000,
      availability: "1 of 1",
      sold: true,
      description: "The nurturing power and creative force of the feminine through acrylic and spray paint on canvas.",
      medium: "Mixed Media: Acrylic & Spray Paint on Canvas",
      dimensions: "36\" x 48\"",
      year: "2023"
    },
    {
      id: 12,
      title: "All I Need Is Love",
      category: "1 of 1s",
      image: "/images/gallery/all-we-need-new.png",
      price: "$1,200",
      priceInCents: 120000,
      availability: "1 of 1",
      sold: true,
      description: "A celebration of love as the foundation of all things through acrylic and plaster on canvas.",
      medium: "Mixed Media: Acrylic & Plaster on Canvas",
      dimensions: "24\" x 48\"",
      year: "2024"
    }
  ];

  const filteredItems = (selectedCategory === 'All' 
    ? shopItems 
    : shopItems.filter(item => item.category === selectedCategory)
  ).slice().sort((a, b) => Number(a.sold) - Number(b.sold));

  // Helper function to check if a 1 of 1 item is already in cart
  const isOneOfOneInCart = (itemId) => {
    return cart.some(cartItem => cartItem.id === itemId);
  };

  return (
    <div className="shop">
      <SEO 
        title="Shop"
        description="Original works and limited edition prints, each created with intention and love. Discover pieces that speak to your soul."
        image="/images/gallery/value-is-still-new.png"
        url="/shop"
      />
      <div className="container">
        {/* Header */}
        <div className="shop-header">
          <h1 className="shop-title">ART <span className="brick-font">Drops</span></h1>
          <p className="shop-subtitle">
            Original works and limited edition prints, each created with intention and love.
          </p>
        </div>

        {/* Category Filters */}
        <div className="shop-filters">
          {categories.map((category) => (
            <button
              key={category}
              className={`shop-filter-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Shop Grid */}
        <div className="shop-grid">
          {filteredItems.map((item) => (
            <div key={item.id} className={`shop-item ${item.sold ? 'sold' : ''}`}>
              <div className="item-image-container">
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="item-image"
                />
                {item.sold && <div className="sold-banner">SOLD</div>}
                <div className="item-overlay">
                  <div className="item-availability">{item.availability}</div>
                  <div className="item-details">
                    <h3 className="item-title">{item.title}</h3>
                    <p className="item-price">{item.price}</p>
                    <p className="item-medium">{item.medium}</p>
                    <p className="item-dimensions">{item.dimensions}</p>
                  </div>
                </div>
              </div>
              <div className="item-info">
                <h3 className="item-title-small">{item.title}</h3>
                <p className="item-price-small">{item.price}</p>
                <p className="item-description">{item.description}</p>
                <button 
                  className={`btn btn-accent ${item.sold || (item.category === '1 of 1s' && isOneOfOneInCart(item.id)) ? 'disabled' : ''}`} 
                  disabled={item.sold || (item.category === '1 of 1s' && isOneOfOneInCart(item.id))}
                  onClick={() => {
                    if (!item.sold && !(item.category === '1 of 1s' && isOneOfOneInCart(item.id))) {
                      if (item.category === 'Prints') {
                        setPrintOptionsModal({ isOpen: true, item });
                      } else {
                        // For 1 of 1s, add directly to cart
                        addToCart(item);
                        openCart(); // Open cart slideout immediately
                      }
                    }
                  }}
                >
                  {item.sold ? 'Sold' : 
                   item.category === '1 of 1s' && isOneOfOneInCart(item.id) ? 'In Cart' :
                   item.category === 'Prints' ? 'Select Options' : 'Add to Cart'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Print Types Explanation - Only show for Prints category */}
        {selectedCategory === 'Prints' && (
          <section className="print-types-info">
            <div className="info-content">
              <h2 className="info-title">Print Types Explained</h2>
              <div className="print-types-grid">
                <div className="print-type">
                  <h3 className="print-type-title">Somerset Giclée</h3>
                  <p className="print-type-description">
                    Museum-quality paper with exceptional color accuracy and archival permanence. 
                    Perfect for framing and display.
                  </p>
                </div>
                <div className="print-type">
                  <h3 className="print-type-title">Canvas Giclée (Stretched)</h3>
                  <p className="print-type-description">
                    Premium canvas stretched over wooden frame, ready to hang immediately. 
                    Has the look and feel of an original painting.
                  </p>
                </div>
                <div className="print-type">
                  <h3 className="print-type-title">Canvas Giclée (Unstretched)</h3>
                  <p className="print-type-description">
                    Same high-quality reproduction as stretched canvas but at a lower price point. 
                    Great for custom framing options.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Shop Info */}
        <section className="shop-info">
          <div className="info-content">
            <h2 className="info-title">About These Works</h2>
            <div className="info-text">
              <p>
                Each piece in this collection is created with deep intention and spiritual connection. 
                When you purchase a work, you're not just buying art—you're bringing home a piece of 
                my heart, my prayers, and my journey.
              </p>
              <p>
                Original works are one-of-a-kind pieces that carry the full energy of their creation. 
                Limited edition prints are carefully reproduced to maintain the essence and quality of 
                the original, each signed and numbered by hand.
              </p>
              <p>
                All works come with care instructions and a personal note. I believe that art should 
                be accessible, which is why I offer both original works and affordable prints. 
                Every piece is created with love and sent with gratitude.
              </p>
            </div>
          </div>
        </section>
      </div>
      
      {/* Print Options Modal */}
      {printOptionsModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <button className="modal-close" onClick={() => setPrintOptionsModal({ isOpen: false, item: null })}>×</button>
            <PrintSizeOptions 
              item={printOptionsModal.item}
              onAddToCart={addToCart}
              onClose={() => setPrintOptionsModal({ isOpen: false, item: null })}
              onOpenCart={openCart}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop; 